import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_BASE_URL from "../../../../Constants";
import { useAuth } from "../../../../contexts/AuthContext";

export default function HistoryEdit() {
    const { callNumber } = useParams();
    const {user} = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        callNumber: '', buildingSite: '', caller: '', date: '', phone: '',
        callPriority: '', takenBy: '', systemDate: '', lastDispatch: '',
        deviceId: '', deviceStatus: '', deviceClass: '', comments: '', completed: ''
    });

    const [eventSource, setEventSource] = useState(null);

    // Fetch call data
    useEffect(() => {
        const fetchCall = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/callcenter/history/editCall/${callNumber}`, {
                    credentials: 'include',
                });
                if (!response.ok) throw new Error('Network response was not ok');
                setFormData(await response.json());
            } catch (error) {
                console.error('Fetch error:', error);
            }
        };
        fetchCall();
    }, [callNumber]);

    // Setup and manage SSE connection
    useEffect(() => {
        const eventSource = new EventSource(`${API_BASE_URL}/sse/subscribe?username=${encodeURIComponent(user.username)}`);
        setEventSource(eventSource);

        eventSource.onmessage = (event) => {
            alert(event.data);

        };
        eventSource.onerror = (err) => {
            console.error('EventSource failed:', err);
            eventSource.close();
        };

        return () => eventSource.close();
    }, [user.username]);


    useEffect(() => {
        const handleBeforeUnload = (event) => {
            // Call API to unlock the record
            clearUserLocks(user.username)
            // Optional: Show a confirmation dialog to the user
            event.preventDefault();
            event.returnValue = ''; // This is necessary for the dialog to show up
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            clearUserLocks(user.username)
        };
    }, []);

    // API CALL TO NOTIFY REQUESTERS
    const notifyRequesters = async (recordId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/notify/requesters?recordId=${encodeURIComponent(recordId + "-history")}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (response.ok) {
                const message = await response.text();
                console.log('Server response:', message);
            } else {
                throw new Error('Failed to notify requesters');
            }
        } catch (error) {
            console.error('An error occurred:', error.message);
        }
    }



    // Function to clear user locks and notify requesters
    const clearUserLocks = async (user) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/record/${encodeURIComponent(user)}/clear-locks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            if (response.ok) {
                console.log('Server response:', await response.text());
                await notifyRequesters(callNumber);
            } else {
                throw new Error('Failed to clear locks');
            }
        } catch (error) {
            console.error('An error occurred:', error.message);
        }
    };

    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`${API_BASE_URL}/api/callcenter/history/updateCall/${callNumber}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(formData)
            });
            if (response.ok) {
                alert('Call History edited successfully');
                clearUserLocks(user.username);
                navigate('/call-center/history');
            } else {
                throw new Error('Failed to edit call history');
            }
        } catch (error) {
            alert(`An error occurred while editing call history: ${error.message}`);
        }
    };

    // Function to handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="max-w-lg mx-auto mt-10 h-full p-6 gradient-bg rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold mb-6 text-left text-white">Edit Call History</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <table className="min-w-full border-separate border-spacing-2">
                    <tbody>
                        <tr>
                            <td>
                                <label className="block text-gray-100">Building Site:</label>
                                <input
                                    type="text"
                                    name="buildingSite"
                                    value={formData.buildingSite}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                            <td>
                                <label className="block text-gray-100">Taken By:</label>
                                <input
                                    type="text"
                                    name="takenBy"
                                    value={formData.takenBy}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label className="block text-gray-100">Call Date:</label>
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                            <td>
                                <label className="block text-gray-100">Caller:</label>
                                <input
                                    type="text"
                                    name="caller"
                                    value={formData.caller}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label className="block text-gray-100">Phone:</label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                            <td>
                                <label className="block text-gray-100">Call Priority:</label>
                                <select name="callPriority" value={formData.callPriority} id="callPriority" onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md">
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label className="block text-gray-100">Device ID:</label>
                                <input
                                    type="text"
                                    name="deviceId"
                                    value={formData.deviceId}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                            <td>
                                <label className="block text-gray-100">System Date:</label>
                                <input
                                    type="date"
                                    name="systemDate"
                                    value={formData.systemDate}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label className="block text-gray-100">Last Dispatch:</label>
                                <input
                                    type="date"
                                    name="lastDispatch"
                                    value={formData.lastDispatch}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                            <td>
                                <label className="block text-gray-100">Device Status:</label>
                                <input
                                    type="text"
                                    name="deviceStatus"
                                    value={formData.deviceStatus}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label className="block text-gray-100">Class:</label>
                                <input
                                    type="text"
                                    name="deviceClass"
                                    value={formData.deviceClass}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-md"
                                />
                            </td>
                            <td>
                                <label className="block text-gray-100">Completed:</label>
                                <select name="completed" value={formData.completed} onChange={handleInputChange} className="w-full px-3 py-2 border rounded-md">
                                    <option value="No" >No</option>
                                    <option value="Yes" >Yes</option>
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2}>
                                <label className="block text-gray-100">Comments</label>
                                <textarea
                                    name="comments"
                                    id="comments"
                                    className="w-full px-3 py-2 border rounded-md"
                                    value={formData.comments}
                                    onChange={handleInputChange}
                                ></textarea>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <button
                    type="submit"
                    className="w-full py-2 px-4 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                    Edit Call
                </button>
            </form>
        </div>
    );
}
